// Package Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'

// External Module Imports
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'

// Module Imports
import {
  leagueDetailQueryOptions,
  leagueListQueryOptions,
} from '@/modules/league/league.hooks'

export const leagueRoute = createRoute({
  path: '/leagues',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const leagueListRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: '/',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(
      preload,
      'leagues.list',
      'Lig listesine erişim izniniz yok.'
    ),
  head: () => ({ meta: [{ title: 'Ligler' }] }),
  loader: async () => {
    await queryClient.ensureQueryData(
      leagueListQueryOptions({ page: 1, perPage: 10 })
    )
  },
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/league/pages/list-league.page')
  ),
})

export const leagueCreateRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: '/create',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(preload, 'leagues.create', 'Lig oluşturma izniniz yok.'),
  head: () => ({ meta: [{ title: 'Yeni Lig' }] }),
  component: lazyRouteComponent(
    () => import('@/modules/league/pages/create-league.page')
  ),
})

export const leagueDetailRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: '/$id',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(
      preload,
      'leagues.detail',
      'Lig detayına erişim izniniz yok.'
    ),
  head: () => ({ meta: [{ title: 'Lig Detayı' }] }),
  validateSearch: (search: Record<string, unknown>): { group?: number } => {
    const raw = search.group
    const num = typeof raw === 'number' ? raw : raw != null ? Number(raw) : NaN
    return Number.isInteger(num) && num >= 0 ? { group: num } : {}
  },
  loaderDeps: ({ search: { group } }) => ({ group }),
  loader: async ({ params: { id }, deps: { group } }) => {
    await queryClient.ensureQueryData(leagueDetailQueryOptions(id, group ?? 0))
  },
  component: lazyRouteComponent(
    () => import('@/modules/league/pages/league-detail.page')
  ),
})

export const leagueEditRoute = createRoute({
  getParentRoute: () => leagueRoute,
  path: '/$id/edit',
  beforeLoad: async ({ preload }) =>
    routeHasPermission(preload, 'leagues.edit', 'Lig düzenleme izniniz yok.'),
  head: () => ({ meta: [{ title: 'Lig Düzenle' }] }),
  loader: async ({ params: { id } }) => {
    await queryClient.ensureQueryData(leagueDetailQueryOptions(id))
  },
  component: lazyRouteComponent(
    () => import('@/modules/league/pages/edit-league.page')
  ),
})
